<script setup>

import OrderLayout                  from "layouts/OrderLayout.vue";
import CpNotice                     from "components/order/CpNotice.vue";
import ServiceLocation              from "src/composables/ServiceLocation";
import ServiceStartDate             from "src/composables/ServiceStartDate";
import SelectedPlan                 from "src/composables/SelectedPlan";
import Utilities                    from "src/composables/Utilities";
import AvailableDates               from "src/composables/AvailableDates";
import Analytics                    from "src/composables/Analytics";
import { useRouter }                from "vue-router";
import { computed, onMounted, ref } from "vue";
import Tdsps                        from "src/composables/Tdsps";
import { cp_bg_c_cart_2025_02_03 }  from "src/composables/Cart";

const { service_location } = ServiceLocation()
const { action, selected_start_date, all_valid } = ServiceStartDate();
const { selected_plan, is_rhythm, is_just_energy, is_txu_energy, is_apge, is_Constellation } = SelectedPlan();
const { plan_brand, plan_phone_number, plan_hours_string } = Utilities();
const { plan_to_ecommerce } = Utilities();
const { available_dates } = AvailableDates();
const { log_event } = Analytics();
const router = useRouter();

const start_date_form = ref(null);
const date_validator = ref(null);

onMounted(() => {
  if (action.value === 'ASAP' && (is_rhythm.value)) {
    action.value = 'Switch'
  }
  if (['Switch', 'ASAP'].includes(action.value)) {
    switch_option_selected.value = true;
  }
  else {
    action.value = 'Move';
  }
})

const available_date_options = computed(() => {
  if (available_dates.value) {
    if (is_move_selected.value) {
      return available_dates.value.filter(x => x.actions.some(y => y === 'priority_move_in' || y === 'standard_move_in')).map(x => x.date.replace(/-/mg, '/'))
    }
    else {
      if (is_Constellation.value) {
        return available_dates.value.filter(x => x.actions.some(y => y === 'switch')).map(x => x.date.replace(/-/mg, '/')).slice(3)
      }
      return available_dates.value.filter(x => x.actions.some(y => y === 'switch')).map(x => x.date.replace(/-/mg, '/'))
    }
  }
  return [];
})

const available_options = computed(() => {
  const all_options = available_dates.value.map(x => x.actions).flat();
  return all_options
      .filter((val, index) => all_options.indexOf(val) === index)
      .map(x => type_to_string(x))
})

const next = async () => {
  let valid = await start_date_form.value.validate(false);
  if (valid) {
    cp_bg_c_cart_2025_02_03.value.meta_data.selected_start_date = selected_start_date.value
    cp_bg_c_cart_2025_02_03.value.meta_data.action = action.value
    log_event({ name: 'co_add_start_date', cp_cart: cp_bg_c_cart_2025_02_03.value })
    let shipping_tier = 'ASAP'
    if (selected_start_date.value) {
      const date_index = available_dates.value.findIndex(x => x.date === selected_start_date.value);
      shipping_tier = `${ action.value }: ${ date_index }`
    }
    else {
      if ((is_just_energy.value || is_txu_energy.value || is_apge.value) && action.value == 'ASAP') {
        selected_start_date.value = available_dates.value[0].date;
      }
    }
    log_event({
      name: 'add_shipping_info',
      keep_name: true,
      ecommerce: {
        shipping_tier,
        items: [
          plan_to_ecommerce({ plan: selected_plan.value, sort_index: -1 })
        ]
      }
    })
    router.push('/order/customer_information')
  }
}

const meter_type = computed(() => {
  switch (service_location.value.tdsp_ams_indicator) {
    case 'AMSR':
      return 'amsr';
    case 'AMSM':
      return 'amsm';
    default:
      return 'non_standard'
  }
})

const meter_type_upper = computed(() => {
  switch (service_location.value.tdsp_ams_indicator) {
    case 'AMSR':
    case 'AMSM':
      return service_location.value.tdsp_ams_indicator;
  }
})

const is_smart_meter = computed(() => {
  switch (meter_type.value) {
    case 'amsr':
    case 'amsm':
      return true
    default:
      return false;
  }
})

const fees = computed(() => {

  if (selected_plan.value?.tdsp?.fees) {
    const fee_map = selected_plan.value.tdsp.fees[meter_type.value];
    const found = available_dates.value.find(x => x.date === selected_start_date.value);

    console.log(fee_map, found);

    const result = [];
    if (fee_map && found) {
      for (const allowed_action of found.actions) {

        if (is_move_selected.value && ['standard_move_in', 'priority_move_in'].includes(allowed_action)) {
          if (fee_map[allowed_action] >= 0) {
            result.push({
              type: type_to_string(allowed_action),
              amount: fee_map[allowed_action]
            })
          }
        }
        else if (action.value === 'Switch' && allowed_action === 'switch') {
          if (fee_map.self_selected_switch >= 0) {
            result.push({
              type: type_to_string('self_selected_switch'),
              amount: fee_map.self_selected_switch
            })
          }
        }
        else {
          if (fee_map.standard_switch >= 0) {
            result.push({
              type: type_to_string('standard_switch'),
              amount: fee_map.standard_switch
            })
          }
        }
      }
    }
    return result
  }
  return []
})

const switch_option_selected = ref(false);

const is_move_selected = computed({
  get: () => action.value === 'Move',
  set: (val) => val ? action.value = 'Move' : action.value = null
});

const type_to_string = (type) => {
  switch (type) {
    case 'priority_move_in':
      return 'Priority Move-in';
    case 'standard_move_in':
      return 'Standard Move-in';
    case 'standard_switch':
      return 'Standard Switch';
    case 'self_selected_switch':
      return 'Self-selected switch date';
    case 'switch':
      return 'Self-selected switch date';
  }
}

const event_color = (date) => {
  const found = available_dates.value.find(x => x.date === date.replace(/\//g, '-'));
  if (found) {
    if (is_move_selected.value && found.actions.some(x => x === 'priority_move_in')) {
      return 'negative';
    }
  }
  return 'positive';
}

const nav_min_year_month = computed(() => {
  return available_date_options.value[0].substr(0, 7)
})

</script>

<template>
  <order-layout step="date">
    <q-card flat bordered class="q-pa-md q-pa-sm-xl more-rounded-borders" v-if="selected_plan">
      <div>
        <cp-notice v-if="service_location.tdsp_ams_indicator === 'AMSR'" type="info" icon="o_auto_awesome"
                   header="Your home has a smart meter."
                   :lines="['This means we can activate your electricity plan without anyone coming to your home.']"></cp-notice>

        <cp-notice clear type="black" icon="event" header="Service Start Date"
                   :lines="['You can select a future start date and still lock in today\'s rate.']"></cp-notice>

        <q-form ref="start_date_form" @submit.prevent>

          <div class="row justify-between">
            <div class="col-12 col-sm-6" :class="{'col-sm-6': ['Move', 'Switch'].includes(action)}">

              <q-field v-model="action" borderless :rules="[val => !!val || 'Please select an option']" class="wrap"
                       lazy-rules>
                <q-list class="q-mb-md text-100">
                  <q-item @click="action = 'Move'" clickable class="q-pl-none">
                    <q-item-section avatar top>
                      <q-radio v-model="is_move_selected" :val="true"></q-radio>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="text-bold text-black">Start new service</q-item-label>
                      <q-item-label caption class="text-100">
                        You are moving in or setting up new service in your name at this address on a specific date.
                      </q-item-label>
                    </q-item-section>
                  </q-item>

                  <q-item @click="(is_rhythm) ? action = 'Switch' : action = 'ASAP'" clickable class="q-pl-none">
                    <q-item-section avatar top>
                      <q-radio v-model="is_move_selected" :val="false"></q-radio>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="text-bold text-black">Switch existing service</q-item-label>
                      <q-item-label caption class="text-100">
                        You already have service here. Your switch happens automatically - no disruption to
                        your power.
                      </q-item-label>
                    </q-item-section>
                  </q-item>

                  <div v-if="!is_move_selected" style="margin-left: 20px; border-left: 2px solid #03a9f4">
                    <q-item v-if="!is_rhythm" @click="action = 'ASAP'" clickable>
                      <q-item-section avatar top>
                        <q-radio v-model="action" val="ASAP"></q-radio>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="text-bold text-black">Switch - Standard</q-item-label>
                        <q-item-label caption class="text-100">
                          {{
                            is_just_energy ? 'Your switch will occur over the next 2 to 5 days.' : 'Your switch will occur over the next 1 to 7 days.'
                          }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item @click="action = 'Switch'" clickable>
                      <q-item-section avatar top>
                        <q-radio v-model="action" val="Switch"></q-radio>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="text-bold text-black">Switch - Selected Date</q-item-label>
                        <q-item-label caption class="text-100">
                          Your switch will occur on a date you choose.
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </div>
                </q-list>

              </q-field>

            </div>

            <div class="col-12 col-sm-6">
              <div class="flex justify-center q-ml-sm-lg q-mb-xl">

                <!--            <cp-notice type="info" icon="o_auto_awesome" header="You have a smart meter." :lines="['This means we can start your electricity remotely without a technician visit. Go you!']"></cp-notice>-->

                <q-date v-model="selected_start_date" mask="YYYY-MM-DD" :event-color="event_color" no-unset
                        class="full-width text-bold more-rounded-borders" color="primary" style="height: 400px" bordered
                        v-if="['Move', 'Switch'].includes(action)" :options="available_date_options"
                        :navigation-min-year-month="nav_min_year_month" :events="available_date_options"></q-date>

                <div class="text-blue-grey-8 text-0875 q-mt-md full-width">
                  <div class="q-ml-md q-mb-md text-100">
                    <div v-if="is_move_selected && available_date_options">
                      <div v-if="available_options.includes('Priority Move-in')">
                        <q-chip color="negative" dark size="xs">&nbsp;&nbsp;&nbsp;</q-chip>
                        Priority Move-in Available
                      </div>
                      <div v-if="available_options.includes('Standard Move-in')">
                        <q-chip color="positive" dark size="xs">&nbsp;&nbsp;&nbsp;</q-chip>
                        Standard Move-in Available
                      </div>
                    </div>
                    <div v-else-if="action === 'Switch'">
                      <div v-if="available_options.includes('Self-selected switch date')">
                        <q-chip color="positive" dark size="xs">&nbsp;&nbsp;&nbsp;</q-chip>
                        Switch Date Available
                      </div>
                    </div>
                  </div>
                </div>

                <div class="text-blue-grey-8 text-0875 q-mt-md">

                  <div class="q-ml-md q-mb-md text-100" v-if="fees.length">
                    <strong>Fees: </strong>
                    <div v-if="is_smart_meter">
                      There is an {{ meter_type_upper }} meter at this service location.
                    </div>
                    <div v-for="fee in fees">
                  <span v-if="fee.amount > 0">
                    {{ selected_plan.tdsp.short_name }} charges ${{ fee.amount.toFixed(2) }} for a {{ fee.type }}
                    <span v-if="is_smart_meter"> at locations with an {{ meter_type_upper }} meter</span>.
                    This charge will appear on your first bill from {{ selected_plan.product.brand.name }}.
                  </span>
                      <span v-else>
                    {{ selected_plan.tdsp.short_name }} does not charge a fee for a {{ fee.type }}
                    <span v-if="is_smart_meter"> at locations with an {{ meter_type_upper }} meter</span>.
                  </span>
                    </div>
                  </div>

                  <div class="q-ml-md q-mb-md text-100" v-if="action === 'Switch'">
                    Texas Public Utility Commission rules allow you to select a switch date up to 14 days prior to your
                    contract
                    expiration date without an early termination penalty from your current provider.
                  </div>

                  <div class="q-ml-md text-100" v-if="['Move', 'Switch'].includes(action)">
                    <p>
                      If you need service sooner than the earliest date offered, you must call in to complete your
                      enrollment.
                    </p>
                    <p>
                      Call {{ plan_brand(selected_plan) }} at
                      <a :href="`tel:${plan_phone_number(selected_plan)}`">{{
                          plan_phone_number(selected_plan)
                        }}</a><br>
                      {{ plan_hours_string(selected_plan) }} for the fastest service possible.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>


          <q-card-actions>
            <q-space></q-space>
            <div>
              <q-btn icon-right="arrow_forward" label="Continue" no-caps
                     :color="!all_valid ? 'grey' : 'secondary'"
                     :style="all_valid ? 'cursor: pointer' : 'cursor: not-allowed'"
                     class="text-bold rounded-borders-10" padding="10px 25px" unelevated @click="next"
              ></q-btn>
              <q-field id="calendar_validation" ref="date_validator" v-model="all_valid" class="full-width" borderless
                       :rules="[val=> val || 'You must select a date']" lazy-rules>
              </q-field>
            </div>
          </q-card-actions>


        </q-form>
      </div>
    </q-card>
  </order-layout>
</template>

<style scoped>
#calendar_validation >>> .q-field__control {
  min-height: 10px !important;
  height: 14px !important;
}
</style>
